// Error Handler Utility - Centralized error handling for API calls

// Custom error class for API-related errors
export class ApiError extends Error {
  constructor(message, status, code, details = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

// Standardized error codes
export const ERROR_CODES = {
  NETWORK_ERROR: "NETWORK_ERROR",
  TIMEOUT_ERROR: "TIMEOUT_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  BAD_REQUEST: "BAD_REQUEST",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  CONFLICT: "CONFLICT",
  SERVER_ERROR: "SERVER_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
};

// Map HTTP status to error code
const statusToErrorCode = (status) => {
  const mapping = {
    400: ERROR_CODES.BAD_REQUEST,
    401: ERROR_CODES.UNAUTHORIZED,
    403: ERROR_CODES.FORBIDDEN,
    404: ERROR_CODES.NOT_FOUND,
    409: ERROR_CODES.CONFLICT,
    422: ERROR_CODES.VALIDATION_ERROR,
    500: ERROR_CODES.SERVER_ERROR,
    502: ERROR_CODES.SERVICE_UNAVAILABLE,
    503: ERROR_CODES.SERVICE_UNAVAILABLE,
    504: ERROR_CODES.TIMEOUT_ERROR,
  };
  return mapping[status] || ERROR_CODES.UNKNOWN_ERROR;
};

// User-friendly error messages
const USER_MESSAGES = {
  [ERROR_CODES.NETWORK_ERROR]:
    "Unable to connect to the server. Please check your internet connection.",
  [ERROR_CODES.TIMEOUT_ERROR]: "The request took too long. Please try again.",
  [ERROR_CODES.UNAUTHORIZED]: "Please sign in to continue.",
  [ERROR_CODES.FORBIDDEN]: "You do not have permission to perform this action.",
  [ERROR_CODES.SESSION_EXPIRED]:
    "Your session has expired. Please sign in again.",
  [ERROR_CODES.BAD_REQUEST]: "Invalid request. Please check your input.",
  [ERROR_CODES.NOT_FOUND]: "The requested resource was not found.",
  [ERROR_CODES.VALIDATION_ERROR]: "Please check your input and try again.",
  [ERROR_CODES.CONFLICT]:
    "A conflict occurred. The data may have been modified.",
  [ERROR_CODES.SERVER_ERROR]:
    "An unexpected error occurred. Please try again later.",
  [ERROR_CODES.SERVICE_UNAVAILABLE]:
    "The service is temporarily unavailable. Please try again later.",
  [ERROR_CODES.UNKNOWN_ERROR]:
    "An unexpected error occurred. Please try again.",
};

// Get user-friendly error message
export const getUserMessage = (errorCode) => {
  return USER_MESSAGES[errorCode] || USER_MESSAGES[ERROR_CODES.UNKNOWN_ERROR];
};

const normalizeErrorMessages = (messages) => {
  if (Array.isArray(messages)) {
    return Array.from(
      new Set(
        messages.map((message) => String(message ?? "").trim()).filter(Boolean),
      ),
    );
  }

  if (messages && typeof messages === "object") {
    return Array.from(
      new Set(
        Object.values(messages)
          .flatMap((value) => normalizeErrorMessages(value))
          .filter(Boolean),
      ),
    );
  }

  const normalizedMessage = String(messages ?? "").trim();

  return normalizedMessage ? [normalizedMessage] : [];
};

const getStructuredErrorDetails = (data) => {
  const errorDetails = data?.errors ?? data?.Errors;

  if (Array.isArray(errorDetails)) {
    return normalizeErrorMessages(errorDetails);
  }

  if (errorDetails && typeof errorDetails === "object") {
    const normalizedDetails = Object.entries(errorDetails).reduce(
      (acc, [field, messages]) => {
        const normalizedMessages = normalizeErrorMessages(messages);

        if (normalizedMessages.length > 0) {
          acc[field] = normalizedMessages;
        }

        return acc;
      },
      {},
    );

    return Object.keys(normalizedDetails).length > 0 ? normalizedDetails : null;
  }

  return null;
};

export const getApiErrorMessages = (error) => {
  if (error?.details) {
    return normalizeErrorMessages(error.details);
  }

  const data =
    error?.responseData || error?.response?.data || error?.data || error;

  return normalizeErrorMessages(data?.errors ?? data?.Errors);
};

// Handle Axios error and convert to ApiError
export const handleApiError = (error) => {
  if (error instanceof ApiError) {
    return error;
  }

  // Network error (no response received)
  if (!error.response) {
    if (error.code === "ECONNABORTED") {
      return new ApiError(
        getUserMessage(ERROR_CODES.TIMEOUT_ERROR),
        0,
        ERROR_CODES.TIMEOUT_ERROR,
      );
    }

    return new ApiError(
      getUserMessage(ERROR_CODES.NETWORK_ERROR),
      0,
      ERROR_CODES.NETWORK_ERROR,
    );
  }

  const { status, data } = error.response;
  const errorCode = statusToErrorCode(status);

  // Try to get the actual error message from API response first
  const apiMessage =
    data?.message ||
    data?.Message ||
    data?.error ||
    data?.Error ||
    (typeof data === "string" ? data : null);
  const message = apiMessage || getUserMessage(errorCode);

  //
  // Extract validation details if available (for 422 errors)
  // let details = null;
  // if (status === 422 && data?.errors) {
  //   details = Object.entries(data.errors).reduce((acc, [field, messages]) => {
  //     acc[field] = Array.isArray(messages) ? messages : [messages];
  //     return acc;
  //   }, {});
  // }
  //
  const details = getStructuredErrorDetails(data);

  const apiError = new ApiError(message, status, errorCode, details);
  // Preserve original response data for additional context
  apiError.responseData = data;

  return apiError;
};

// Check if error is a specific type
export const isErrorType = (error, errorCode) => {
  if (error instanceof ApiError) {
    return error.code === errorCode;
  }
  return false;
};

// Check if error indicates user should be logged out
export const shouldLogout = (error) => {
  if (error instanceof ApiError) {
    return [ERROR_CODES.UNAUTHORIZED, ERROR_CODES.SESSION_EXPIRED].includes(
      error.code,
    );
  }
  return false;
};

// Check if error is retryable
export const isRetryable = (error) => {
  if (error instanceof ApiError) {
    return [
      ERROR_CODES.TIMEOUT_ERROR,
      ERROR_CODES.NETWORK_ERROR,
      ERROR_CODES.SERVICE_UNAVAILABLE,
    ].includes(error.code);
  }
  return false;
};

// Wrap an async function with error handling
export const withErrorHandling = (fn) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      throw handleApiError(error);
    }
  };
};

export default {
  ApiError,
  ERROR_CODES,
  getUserMessage,
  getApiErrorMessages,
  handleApiError,
  isErrorType,
  shouldLogout,
  isRetryable,
  withErrorHandling,
};
